import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Generate a professional PDF for a quotation or invoice.
 *
 * @param {Object} document   — The quotation or invoice object (populated)
 * @param {'quotation'|'invoice'} type
 * @param {Object} workshopData — Workshop object with businessName, address, phone, logo, etc.
 */
export function generateDocumentPDF(document, type, workshopData = {}) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 15;

  const isInvoice = type === 'invoice';
  const title = isInvoice ? 'FACTURE' : 'DEVIS';
  // Group thousands with a plain ASCII space. toLocaleString('fr-FR') emits a
  // narrow no-break space (U+202F) that the PDF font renders as a "/" — the
  // server-side generator in backend/utils/documentPdf.js already sidesteps it
  // this way, and this copy had been printing "45/000" on every amount.
  const formatMoney = (n) => Math.round(Number(n) || 0)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // ── Colors ──
  const primary = [41, 128, 185];   // Blue
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];

  // ── Header (white background, black text, blue accent line) ──
  doc.setDrawColor(...primary);
  doc.setLineWidth(1);
  doc.line(margin, 42, pageWidth - margin, 42);

  // ── Logo (if exists) ──
  if (workshopData?.logo) {
    try {
      doc.addImage(workshopData.logo, 'JPEG', margin, 5, 22, 22);
    } catch (e) {
      // Skip logo if it fails to load
    }
  }

  // ── Workshop info (top left) ──
  const infoX = workshopData?.logo ? margin + 26 : margin;
  doc.setTextColor(...darkText);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(workshopData?.businessName || 'Atelier', infoX, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  if (workshopData?.address) doc.text(workshopData.address, infoX, 20);
  if (workshopData?.phone) doc.text(`Tel: ${workshopData.phone}`, infoX, 25);
  if (workshopData?.email) doc.text(workshopData.email, infoX, 30);
  if (workshopData?.taxId) doc.text(`N° TVA: ${workshopData.taxId}`, infoX, 35);

  // ── Document title (top right) ──
  doc.setTextColor(...primary);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth - margin, 16, { align: 'right' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  const numPrefix = isInvoice ? 'F' : 'D';
  const docNumber = document.documentNumber
    || (Number.isInteger(document.sequenceNumber)
      ? `${numPrefix}-${String(document.sequenceNumber).padStart(5, '0')}`
      : (document._id ? `${numPrefix}-${document._id.slice(-8).toUpperCase()}` : `${numPrefix}-----`));
  doc.text(`N° ${docNumber}`, pageWidth - margin, 24, { align: 'right' });

  const docDate = document.createdAt
    ? new Date(document.createdAt).toLocaleDateString('fr-FR')
    : new Date().toLocaleDateString('fr-FR');
  doc.text(`Date: ${docDate}`, pageWidth - margin, 30, { align: 'right' });

  if (isInvoice) {
    const statusText = document.paid ? 'PAYÉE' : 'NON PAYÉE';
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(statusText, pageWidth - margin, 36, { align: 'right' });
  }

  y = 52;

  // ── Client & Vehicle info ──
  doc.setTextColor(...darkText);
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 22, 3, 3, 'F');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('CLIENT', margin + 5, y + 6);
  doc.text('VÉHICULE', pageWidth / 2 + 5, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  const clientName = document.client?.name || document.clientName || '—';
  doc.text(clientName, margin + 5, y + 13);
  if (document.client?.email) {
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text(document.client.email, margin + 5, y + 18);
  }

  doc.setFontSize(9);
  doc.setTextColor(...darkText);
  const vehicleInfo = document.vehicle
    ? `${document.vehicle.brand || ''} ${document.vehicle.model || ''}`
    : '—';
  const plateNumber = document.vehicle?.plateNumber || '';
  doc.text(vehicleInfo, pageWidth / 2 + 5, y + 13);
  if (plateNumber) {
    doc.setFont('helvetica', 'bold');
    doc.text(plateNumber, pageWidth / 2 + 5, y + 18);
  }

  // Kilométrage the document was drawn up at. The devis stores its own
  // snapshot; falling back to the vehicle covers documents written before that
  // field existed. Printed to the right of the plate so it does not push the
  // block taller.
  const odometer = Number(document.odometerReading || document.vehicle?.odometerReading) || 0;
  if (odometer > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text(
      `${formatMoney(odometer)} km`,
      pageWidth - margin - 5,
      y + 18,
      { align: 'right' }
    );
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
  }

  y += 30;

  // ── Items table ──
  const items = document.items || [];
  const tableBody = items.map((item, i) => [
    i + 1,
    item.description || '—',
    item.reference || '',
    item.quantity || 1,
    `${formatMoney(item.clientPrice || item.price)} FCFA`,
    `${formatMoney((item.quantity || 1) * (item.clientPrice || item.price || 0))} FCFA`,
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'Désignation', 'Réf', 'Qté', 'P.U.', 'Total']],
    body: tableBody,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: darkText,
      lineColor: [220, 220, 220],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 10, halign: 'center' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 25 },
      3: { cellWidth: 15, halign: 'center' },
      4: { cellWidth: 30, halign: 'right' },
      5: { cellWidth: 35, halign: 'right' },
    },
    alternateRowStyles: {
      fillColor: [250, 250, 252],
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ── Totals section ──
  const totalsX = pageWidth - margin - 80;
  const valX = pageWidth - margin;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);

  const drawTotal = (label, value, bold = false) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(bold ? darkText[0] : grayText[0], bold ? darkText[1] : grayText[1], bold ? darkText[2] : grayText[2]);
    doc.text(label, totalsX, y);
    doc.text(`${formatMoney(value)} FCFA`, valX, y, { align: 'right' });
    y += 7;
  };

  const hasTax = (document.tax || 0) > 0;

  // Discount (remise): items table shows full client prices; show full subtotal
  // then subtract the remise so the discounted total reconciles.
  const discountPct = Number(document.discountPercent) || 0;
  const clientSubtotalFull = (document.items || []).reduce(
    (s, it) => s + (Number(it.clientPrice || it.price) || 0) * (Number(it.quantity) || 1), 0
  );
  const discountAmount = discountPct > 0 ? Math.round(clientSubtotalFull * discountPct / 100) : 0;

  if (hasTax) {
    drawTotal('Sous-total HT', document.subtotal || 0);
    drawTotal('TVA (18%)', document.tax);
  } else if (discountPct > 0) {
    drawTotal('Sous-total', Math.round(clientSubtotalFull));
  }

  if (discountPct > 0) {
    drawTotal(`Remise ${discountPct}%`, -discountAmount);
  }

  // Divider line
  doc.setDrawColor(200, 200, 200);
  doc.line(totalsX, y - 3, valX, y - 3);

  doc.setFontSize(12);
  const displayTotal = document.clientTotal || document.total || 0;
  drawTotal(hasTax ? 'Total TTC' : 'Total', displayTotal, true);
  doc.setFontSize(10);

  // Down payment the client is ASKED to pay upfront — not yet paid, so it does
  // NOT reduce the total. Shown for information only.
  if (document.depositAmount) {
    drawTotal('Acompte demandé', document.depositAmount);
  }

  if (isInvoice && document.paidAmount > 0) {
    drawTotal('Déjà payé', document.paidAmount);
    drawTotal('Solde', displayTotal - (document.paidAmount || 0), true);
  }

  // ── Note ──
  const note = document.note || document.clientNote;
  if (note) {
    y += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('Note:', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const lines = doc.splitTextToSize(note, pageWidth - margin * 2);
    doc.text(lines, margin, y);
    y += lines.length * 5;
  }

  // ── Footer ──
  const footerY = doc.internal.pageSize.getHeight() - 10;
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  doc.setFont('helvetica', 'italic');
  doc.text('Généré via 1PARC — www.1parc.com', pageWidth / 2, footerY, { align: 'center' });

  // ── Save ──
  const prefix = isInvoice ? 'Facture' : 'Devis';
  const fileName = `${prefix}-${docNumber}-${docDate.replace(/\//g, '-')}.pdf`;
  doc.save(fileName);
}
